import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Drawer, Form, Input, InputNumber, Modal, Select, Space, message } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import { showError } from 'utils/exceptionUtils';
import {
  CREATE_DATA_RETENTION_POLICY,
  DataRetentionPolicyItem,
  LIST_RETAINABLE_TABLES,
  ListRetainableTablesResponse,
  RetainableTableItem,
  UPDATE_DATA_RETENTION_POLICY,
} from '../graphql/dataRetentionQueries';
import { validateRetentionDays } from '../utils/retentionDaysValidator';
import { renderPhysicalDeleteWarning } from '../utils/physicalDeleteWarning';

interface Props {
  visible: boolean;
  /** 传 null 表示新建 */
  policy: DataRetentionPolicyItem | null;
  /** 已存在策略的 target，用于禁用重复项（同一张表只能有一条策略） */
  existingTargets: string[];
  onClose: () => void;
  onSuccess: () => void;
}

interface FormValues {
  target: string;
  retentionDays: number;
  batchSize: number;
  maxBatchesPerRun: number;
  batchDelayMs: number;
  remark?: string;
}

const PolicyDrawer: React.FC<Props> = ({ visible, policy, existingTargets, onClose, onSuccess }) => {
  const [form] = Form.useForm<FormValues>();
  const isEdit = !!policy;
  const [selectedTarget, setSelectedTarget] = useState<string | undefined>(undefined);

  const { data: tablesData } = useQuery<ListRetainableTablesResponse>(LIST_RETAINABLE_TABLES, {
    skip: !visible,
    fetchPolicy: 'cache-first',
  });
  const tables = useMemo(() => tablesData?.listRetainableTables ?? [], [tablesData]);

  const currentTable: RetainableTableItem | undefined = useMemo(
    () => tables.find((t) => t.name === (isEdit ? policy?.target : selectedTarget)),
    [tables, isEdit, policy, selectedTarget],
  );

  const [createPolicy, { loading: creating }] = useMutation(CREATE_DATA_RETENTION_POLICY, {
    onCompleted: () => {
      message.success('创建成功，策略默认为停用状态，需手动启用');
      onSuccess();
    },
    onError: showError,
  });

  const [updatePolicy, { loading: updating }] = useMutation(UPDATE_DATA_RETENTION_POLICY, {
    onCompleted: () => {
      message.success('保存成功');
      onSuccess();
    },
    onError: showError,
  });

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setSelectedTarget(undefined);
      return;
    }
    if (policy) {
      setSelectedTarget(policy.target);
      form.setFieldsValue({
        target: policy.target,
        retentionDays: policy.retentionDays,
        batchSize: policy.batchSize,
        maxBatchesPerRun: policy.maxBatchesPerRun,
        batchDelayMs: policy.batchDelayMs,
        remark: policy.remark ?? undefined,
      });
    }
  }, [visible, policy, form]);

  /** 选中目标表后，用该表的建议值预填 */
  const handleTargetChange = (value: string) => {
    setSelectedTarget(value);
    const table = tables.find((t) => t.name === value);
    if (table) {
      form.setFieldsValue({
        retentionDays: table.defaultRetentionDays,
        batchSize: table.defaultBatchSize,
        maxBatchesPerRun: 50,
        batchDelayMs: 200,
      });
    }
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const tableName = currentTable?.tableName ?? values.target;

    Modal.confirm({
      title: isEdit ? '确认保存修改' : '确认创建策略',
      icon: <ExclamationCircleOutlined />,
      okType: 'danger',
      okText: '确认',
      cancelText: '取消',
      width: 560,
      content: renderPhysicalDeleteWarning(tableName, values.retentionDays),
      onOk: () => {
        if (isEdit && policy) {
          return updatePolicy({
            variables: {
              input: {
                id: policy.id,
                retentionDays: values.retentionDays,
                batchSize: values.batchSize,
                maxBatchesPerRun: values.maxBatchesPerRun,
                batchDelayMs: values.batchDelayMs,
                remark: values.remark || null,
              },
            },
          });
        }
        return createPolicy({
          variables: {
            input: {
              target: values.target,
              retentionDays: values.retentionDays,
              batchSize: values.batchSize,
              maxBatchesPerRun: values.maxBatchesPerRun,
              batchDelayMs: values.batchDelayMs,
              remark: values.remark || null,
            },
          },
        });
      },
    });
  };

  return (
    <Drawer
      title={isEdit ? `编辑策略 - ${policy?.tableName}` : '新建数据保留策略'}
      width={560}
      open={visible}
      onClose={onClose}
      maskClosable={false}
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="该配置会导致数据被物理删除且不可恢复"
        description="目标表来自后端白名单，保留天数不得低于各表的安全下限。删除不会归还磁盘空间。"
      />

      <Form form={form} layout="vertical">
        <Form.Item
          name="target"
          label="目标表"
          rules={[{ required: true, message: '请选择目标表' }]}
          extra={currentTable ? `判断依据：${currentTable.timeColumn}` : undefined}
        >
          <Select
            placeholder="请选择目标表"
            disabled={isEdit}
            onChange={handleTargetChange}
            options={tables.map((t) => ({
              label: `${t.tableName} - ${t.description}`,
              value: t.name,
              // 同一张表只能有一条策略
              disabled: !isEdit && existingTargets.includes(t.name),
            }))}
          />
        </Form.Item>

        <Form.Item
          name="retentionDays"
          label="保留天数"
          rules={[
            { required: true, message: '请填写保留天数' },
            {
              validator: (_, value) => {
                const error = validateRetentionDays(value, currentTable?.minRetentionDays);
                return error ? Promise.reject(new Error(error)) : Promise.resolve();
              },
            },
          ]}
          extra={
            currentTable
              ? `该表安全下限 ${currentTable.minRetentionDays} 天；低于下限时后端会按下限执行`
              : undefined
          }
        >
          <InputNumber min={currentTable?.minRetentionDays ?? 1} style={{ width: '100%' }} addonAfter="天" />
        </Form.Item>

        <Form.Item
          name="batchSize"
          label="单批行数"
          rules={[{ required: true, message: '请填写单批行数' }]}
          extra="行较大或索引较多的表应取小值，避免单批 DELETE 事务过长拖高从库延迟"
        >
          <InputNumber min={50} max={2000} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="maxBatchesPerRun"
          label="单轮最大批次"
          rules={[{ required: true, message: '请填写单轮最大批次' }]}
          extra="上限而非固定值，无超期数据时会提前结束"
        >
          <InputNumber min={1} max={500} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="batchDelayMs"
          label="批间延时"
          rules={[{ required: true, message: '请填写批间延时' }]}
          extra="批与批之间的削峰间隔，0 表示不延时"
        >
          <InputNumber min={0} max={10000} style={{ width: '100%' }} addonAfter="ms" />
        </Form.Item>

        <Form.Item name="remark" label="备注">
          <Input.TextArea rows={2} maxLength={500} showCount />
        </Form.Item>

        <Form.Item>
          <div className="flex justify-end">
            <Space>
              <Button onClick={onClose}>取消</Button>
              <Button type="primary" loading={creating || updating} onClick={handleSubmit}>
                保存
              </Button>
            </Space>
          </div>
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default PolicyDrawer;
